// -----------------------------------------------------------------
// Line-item + bill-footer derivation rules for Bulk Capture review.
//
// Rules (locked in the product spec):
//   • line_total is PRE-TAX.
//   • line_total = quantity * unit_price
//   • line_tax   = line_total * (tax_pct or 0) / 100
//   • subtotal      = Σ line_total
//   • tax_total     = Σ line_tax
//   • total_amount  = subtotal + tax_total - discount + round_off
//
// Origin lattice:
//   EDITED beats DERIVED beats OCR.
//   Manually-edited fields must survive later derivations unless the
//   operator re-edits that same field.
//
// Precision:
//   currency  → 2 decimals (paise)
//   quantity  → up to 3 decimals allowed (weight/volume goods)
// -----------------------------------------------------------------

const isPositive = (v) => typeof v === "number" && Number.isFinite(v) && v > 0;
const isPresent  = (v) => v !== null && v !== undefined && v !== "" && !Number.isNaN(v);

/** Round to 2 decimal places (currency). */
export function round2(v) {
  if (!Number.isFinite(v)) return v;
  return Math.round(v * 100) / 100;
}

/** Round to 3 decimal places (quantity). */
export function round3(v) {
  if (!Number.isFinite(v)) return v;
  return Math.round(v * 1000) / 1000;
}

/** Set the origin marker on a field IF the field itself is present. */
function withOrigin(item, field, origin) {
  return { ...item, [`${field}_origin`]: origin };
}

/**
 * Apply the derivation rule when the operator just edited `editedField`.
 * Returns a NEW line-item object (does not mutate).
 *
 * Contract (from spec, quoted verbatim):
 *   Edited quantity
 *     - If unit_price exists          → line_total = qty * unit_price (DERIVED)
 *     - Else if line_total + qty > 0  → unit_price = line_total / qty  (DERIVED)
 *   Edited unit_price
 *     - If quantity exists            → line_total = qty * unit_price (DERIVED)
 *     - Else if line_total + up > 0   → quantity   = line_total / unit_price (DERIVED)
 *   Edited line_total
 *     - If qty > 0                    → unit_price = line_total / qty  (DERIVED)
 *     - Else if unit_price > 0        → quantity   = line_total / unit_price (DERIVED)
 *   Edited tax_pct                    → NO change to line_total
 *
 * We NEVER overwrite a field that carries origin "EDITED" (unless the
 * operator is editing that same field again — handled by the caller
 * setting `<field>_origin: "EDITED"` before calling derive).
 */
export function deriveLineItem(item, editedField) {
  if (!item) return item;
  const qty = item.quantity;
  const up  = item.unit_price;
  const lt  = item.line_total;

  const canWrite = (field) => item[`${field}_origin`] !== "EDITED"
                              || field === editedField;

  let out = { ...item };

  if (editedField === "quantity") {
    if (isPresent(qty) && isPositive(up) && canWrite("line_total")) {
      out.line_total = round2(Number(qty) * Number(up));
      out.line_total_origin = "DERIVED";
    } else if (isPositive(qty) && isPresent(lt) && !isPresent(up) && canWrite("unit_price")) {
      out.unit_price = round2(Number(lt) / Number(qty));
      out.unit_price_origin = "DERIVED";
    }
  } else if (editedField === "unit_price") {
    if (isPresent(up) && isPresent(qty) && canWrite("line_total")) {
      out.line_total = round2(Number(qty) * Number(up));
      out.line_total_origin = "DERIVED";
    } else if (isPositive(up) && isPresent(lt) && !isPresent(qty) && canWrite("quantity")) {
      out.quantity = round3(Number(lt) / Number(up));
      out.quantity_origin = "DERIVED";
    }
  } else if (editedField === "line_total") {
    if (isPositive(qty) && isPresent(lt) && canWrite("unit_price")) {
      out.unit_price = round2(Number(lt) / Number(qty));
      out.unit_price_origin = "DERIVED";
    } else if (isPositive(up) && isPresent(lt) && !isPresent(qty) && canWrite("quantity")) {
      out.quantity = round3(Number(lt) / Number(up));
      out.quantity_origin = "DERIVED";
    }
  }
  // Edited tax_pct: NO change to line_total (per spec — tax is bill-level).

  return out;
}

/**
 * Recompute bill footer from an array of line items.
 * Returns { subtotal, tax_total, total_amount } (all rounded to 2dp).
 *
 *   subtotal      = Σ line_total (pre-tax)
 *   tax_total     = Σ line_total × tax_pct / 100
 *   total_amount  = subtotal + tax_total - discount + round_off
 *
 * `discount` and `round_off` come from the bill row and default to 0.
 */
export function computeBillFooter(lineItems, { discount = 0, round_off = 0, bill_tax_total = null,
                                               printed_total = null, charges = null } = {}) {
  const items = Array.isArray(lineItems) ? lineItems : [];
  let gross = 0;
  let rateTax = 0;
  let anyLineRate = false;
  let printedTaxable = 0;
  let printedLineTax = 0;
  let allTaxable = items.length > 0;
  let allLineTax = items.length > 0;
  for (const li of items) {
    const lt = Number(li?.line_total);
    if (Number.isFinite(lt)) {
      gross += lt;
      // The UI writes `tax_pct`; OCR and the backend write `tax_percent`. Both are the same fact.
      // An explicitly blank field is NOT a zero rate — Number(null) === 0 is finite, so reject blanks first.
      const raw = li?.tax_pct ?? li?.tax_percent;
      const tp = (raw === null || raw === undefined || raw === "") ? NaN : Number(raw);
      if (Number.isFinite(tp)) {
        rateTax += lt * tp / 100;
        anyLineRate = true;
      }
    }
    const tv = Number(li?.taxable_amount ?? li?.item_taxable_amount);
    if (Number.isFinite(tv)) printedTaxable += tv; else allTaxable = false;
    const tx = Number(li?.tax_amount ?? li?.item_tax_amount);
    if (Number.isFinite(tx)) printedLineTax += tx; else allLineTax = false;
  }
  const d = Number.isFinite(Number(discount)) ? Number(discount) : 0;
  const r = Number.isFinite(Number(round_off)) ? Number(round_off) : 0;
  // Everything printed below the tax line (TCS, freight, round off, discount) moves the total too.
  // Printed rows win; the header round-off / discount still apply when the bill prints no such row.
  const additive = charges
    ? round2(charges.tcs + charges.charges
             + (charges.round_off || r) - (charges.discount || d))
    : (r - d);
  // Tax evidence, strongest first: the printed per-line tax column, then the bill-level tax the document
  // states once at the foot, then the printed per-line rates.
  const billTax = charges && charges.hasRows ? charges.tax
    : (Number.isFinite(Number(bill_tax_total)) ? Number(bill_tax_total) : null);
  let tax_total;
  if (allLineTax && printedLineTax > 0) tax_total = printedLineTax;
  else if (billTax !== null && billTax > 0) tax_total = billTax;
  else tax_total = anyLineRate ? rateTax : (billTax ?? 0);

  // A printed line amount may ALREADY contain the tax. Decide which, from the printed evidence only:
  // the per-line taxable column when the supplier prints one, otherwise whichever of the two hypotheses
  // reproduces the printed bill total. Never add the tax a second time on top of a gross amount.
  const printed = Number(printed_total);
  let subtotal;
  let inclusive;
  if (allTaxable && printedTaxable > 0) {
    subtotal = printedTaxable;
    inclusive = gross > printedTaxable + 0.05;
  } else if (Number.isFinite(printed) && tax_total > 0) {
    const exclusiveFits = Math.abs(printed - (gross + tax_total + additive)) <= 0.05;
    const inclusiveFits = Math.abs(printed - (gross + additive)) <= 0.05;
    inclusive = inclusiveFits && !exclusiveFits;
    subtotal = inclusive ? gross - tax_total : gross;
  } else {
    inclusive = false;
    subtotal = gross;
  }
  const total_amount = subtotal + tax_total + additive;
  return {
    subtotal:     round2(subtotal),
    tax_total:    round2(tax_total),
    total_amount: round2(total_amount),
    gross_line_total: round2(gross),
    lines_tax_inclusive: inclusive,
  };
}

/**
 * Total tax the DOCUMENT prints, read from the printed charge rows when the bill states no single tax
 * figure. Only tax rows count: TCS (collected at source), discounts, round-off and freight are not tax.
 */
const TAX_LABEL = /\b(c\.?\s?gst|s\.?\s?gst|i\.?\s?gst|u\.?\s?gst|gst|vat|cess)\b/i;
const NOT_TAX_LABEL = /\b(tcs|tax collected at source|discount|round|freight|delivery|packing|handling|service)\b/i;

export function printedTaxTotal(printedCharges) {
  const rows = Array.isArray(printedCharges) ? printedCharges : [];
  const label = (c) => String(c?.printed_label ?? c?.label ?? "");
  const isTax = (c) => Number.isFinite(Number(c?.amount))
    && !NOT_TAX_LABEL.test(label(c)) && TAX_LABEL.test(label(c));
  // A summary "Total Tax" row is the SUM of the component rows, never an extra tax.
  const summary = /total\s*tax|tax\s*total/i;
  const components = rows.filter((c) => isTax(c) && !summary.test(label(c)));
  const picked = components.length ? components : rows.filter(isTax);
  if (!picked.length) return null;
  return round2(picked.reduce((a, c) => a + Number(c.amount), 0));
}

/**
 * Classify a printed charge row from its LABEL, which is the only authority. Mirrors the canonical
 * backend classifier (bill_engine/financial_components.py) — TCS is matched before any generic tax word,
 * and nothing is ever inferred from the vendor or the country.
 */
const CHARGE_RULES = [
  ["TCS", /\bt\.?\s?c\.?\s?s\.?\b|tax\s+collect(?:ed|ion)\s+at\s+source/i],
  ["CGST", /\bc\.?\s?gst\b|central\s+gst/i],
  ["SGST", /\bs\.?\s?gst\b|state\s+gst|\bu\.?\s?gst\b|union\s+territory\s+gst/i],
  ["IGST", /\bi\.?\s?gst\b|integrated\s+gst/i],
  ["GST_CESS", /\bcess\b/i],
  ["GST", /\bgst\b|goods\s+and\s+services?\s+tax/i],
  ["VAT", /\bv\.?\s?a\.?\s?t\.?\b|value\s+added\s+tax/i],
  ["DELIVERY_FREIGHT", /deliver\w*|freight|\bfrt\b|transport\w*|cartage|carriage|shipping|courier|\bdel\b/i],
  ["PACKING", /pack\w*|\bpkg\b|\bpkng\b/i],
  ["HANDLING", /handl\w*|\bhdl\b/i],
  ["SERVICE_CHARGE", /service\s+charge|\bsvc\s+chg\b|service\s+chg/i],
  ["DISCOUNT", /discount|\bdisc\b|rebate|\bless\b|scheme/i],
  ["ROUND_OFF", /round(?:ing)?[\s\-_]*off|\br\s*\/\s*o\b|roundoff|\brnd\b/i],
];

// A summary row ("Total Tax", "Total GST") is the SUM of the component rows printed above it, never an
// extra charge: counting it as well would double the tax.
const SUMMARY_ROW = /total\s*tax|tax\s*total|total\s*gst|gst\s*total/i;

export function classifyPrintedCharge(label) {
  const text = String(label ?? "");
  for (const [kind, rx] of CHARGE_RULES) if (rx.test(text)) return kind;
  return "UNCLASSIFIED";
}

const TAX_KINDS = ["GST", "CGST", "SGST", "UGST", "IGST", "VAT", "GST_CESS"];

/**
 * Group the printed charge rows into the canonical buckets the reconciliation formula uses:
 *   taxable - discount + tax + TCS + other charges + round off = bill total
 */
export function summarizeCharges(printedCharges) {
  const rows = (Array.isArray(printedCharges) ? printedCharges : [])
    .map((c, index) => ({
      index,
      label: String(c?.printed_label ?? c?.label ?? ""),
      amount: Number.isFinite(Number(c?.amount)) ? Number(c.amount) : null,
      rate: Number.isFinite(Number(c?.rate)) ? Number(c.rate) : null,
      kind: classifyPrintedCharge(c?.printed_label ?? c?.label),
      summary: SUMMARY_ROW.test(String(c?.printed_label ?? c?.label ?? "")),
      edited: c?.amount_origin === "EDITED",
      ocrAmount: Number.isFinite(Number(c?.ocr_amount)) ? Number(c.ocr_amount) : null,
    }));
  const counted = rows.filter((r) => !r.summary);
  const sum = (pred) => counted.reduce((a, r) => (pred(r) && r.amount != null ? a + r.amount : a), 0);
  const tax = sum((r) => TAX_KINDS.includes(r.kind));
  const tcs = sum((r) => r.kind === "TCS");
  const discount = Math.abs(sum((r) => r.kind === "DISCOUNT"));
  const round_off = sum((r) => r.kind === "ROUND_OFF");
  const charges = sum((r) => ["DELIVERY_FREIGHT", "PACKING", "SERVICE_CHARGE", "OTHER_CHARGE",
                              "UNCLASSIFIED"].includes(r.kind));
  return {
    rows,
    hasRows: rows.length > 0,
    tax: round2(tax),
    tcs: round2(tcs),
    discount: round2(discount),
    round_off: round2(round_off),
    charges: round2(charges),
    // Everything printed BELOW the tax line that still moves the bill total.
    additive: round2(tcs + charges + round_off - discount),
  };
}

/**
 * Difference between the OCR-declared total and the recomputed total.
 * Positive = OCR is higher; negative = OCR is lower. `null` if either
 * side is not a finite number.
 */
export function totalsMismatch(ocrTotal, computedTotal) {
  const a = Number(ocrTotal);
  const b = Number(computedTotal);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return round2(a - b);
}
